import { Fragment, type FunctionComponent, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { Button } from '~/ui/core/button/Button';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';

import type { JobItemDetails } from '../../api/use-job-item-details';
import { useArchivedItemActions } from '../../hooks/use-archived-item-actions';
import { getJobTypeTranslation } from '../../hooks/use-review-action';
import { ButtonContainer, Container, Text, TextContainer, variants } from './notification-bar-styled';

export const ArchivedNotification: FunctionComponent<{
    readonly architectureId: string;
    readonly item: JobItemDetails;
    readonly itemType: 'job-level' | 'job-role';
    readonly onDeleteComplete?: () => void;
    readonly onRestoreComplete?: () => void;
}> = ({ architectureId, item, itemType, onDeleteComplete, onRestoreComplete }) => {
    const { translate } = useContext(ApplicationContext);

    const { handleDelete, handleRestore, isDeleteDialogOpen, setIsDeleteDialogOpen } = useArchivedItemActions({
        architectureId,
        item,
        itemType,
        onDeleteComplete,
        onRestoreComplete,
    });

    const jobType = getJobTypeTranslation(itemType, translate);

    return (
        <Fragment>
            <Container
                key="archived"
                animate={variants.animate}
                exit={variants.exit}
                initial={variants.initial}
                style={{
                    backgroundColor: 'var(--color-status-neutral-surface)',
                    borderColor: 'var(--color-status-neutral-border)',
                }}
            >
                <TextContainer style={{ color: 'var(--color-status-neutral-text)' }}>
                    <Text>{translate('pages.job-architecture-v2.archived-notification', { jobType })}</Text>
                </TextContainer>
                <ButtonContainer>
                    <Button variant="primary" onClick={handleRestore}>
                        {translate('pages.job-architecture-v2.restore')}
                    </Button>
                    <Button variant="destructive" onClick={() => setIsDeleteDialogOpen(true)}>
                        {translate('common.delete')}
                    </Button>
                </ButtonContainer>
            </Container>
            <ConfirmationDialog
                cancelButtonLabel={translate('common.cancel')}
                confirmButtonLabel={translate('common.delete')}
                confirmButtonVariant="destructive"
                description={translate('pages.job-architecture-v2.delete-permanently-description')}
                open={isDeleteDialogOpen}
                title={translate('pages.job-architecture-v2.delete-permanently-title')}
                onCancel={() => setIsDeleteDialogOpen(false)}
                onConfirm={handleDelete}
            />
        </Fragment>
    );
};
